import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const formatCurrency = (value) => 
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

export default function BankBalance(props) {
    console.log("props.data ", props.data)
  // Explicitly filter the array to ensure ONLY accounts belonging to the "BANK" group are processed
  const ledgerData = (props.data || []).filter(item => item.GROUP === "BANK");

  // Dynamically calculate chart width based only on the filtered bank accounts
  const calculatedWidth = Math.max(800, (ledgerData.length * 120) + 100);

  return (
    <div style={{ 
      padding: '4px', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', 
      margin: '0 auto',
      backgroundColor: '#f8fafc',
      color: '#334155',
      borderRadius: '16px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.03)'
    }}>
     
      {/* 1. DATA TABLE SECTION */}
      <div style={{ 
        overflowX: 'auto', 
        marginBottom: '40px', 
        backgroundColor: '#ffffff', 
        border: '1px solid #e2e8f0', 
        borderRadius: '12px',
        maxHeight:'300px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.02)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f1f5f9' }}>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>Account Name</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>Code</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#475569', fontSize: '12px', letterSpacing: '0.05em' }}>Opening Balance</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#16a34a', fontSize: '12px', letterSpacing: '0.05em' }}>Receipts (+)</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#dc2626', fontSize: '12px', letterSpacing: '0.05em' }}>Payments (-)</th>
              <th style={{ padding: '16px', fontWeight: '600', color: '#0f172a', fontSize: '12px', letterSpacing: '0.05em' }}>Closing Balance</th>
            </tr>
          </thead>
          <tbody>
            {ledgerData.map((row) => (
              <tr 
                key={row.Account_Code} 
                style={{ 
                  borderBottom: '1px solid #e2e8f0', 
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <td style={{ padding: '16px', fontWeight: '600', color: '#0f172a', maxWidth: '280px' }}>{row.Account_Name}</td>
                <td style={{ padding: '16px', color: '#64748b', fontSize: '13px' }}>{row.Account_Code}</td>
                <td style={{ padding: '16px', color: '#475569' }}>{row.Opening_Balance}</td>
                <td style={{ padding: '16px', color: '#16a34a', fontWeight: row.Receipts > 0 ? '500' : '400' }}>{row.Receipts}</td>
                <td style={{ padding: '16px', color: '#dc2626', fontWeight: row.Payments > 0 ? '500' : '400' }}>{row.Payments}</td>
                <td style={{ 
                  padding: '16px', 
                  fontWeight: '700', 
                  color: row.Closing_Balance >= 0 ? '#0f172a' : '#b91c1c' 
                }}>
                  {row.Closing_Balance}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 2. GRAPH SECTION */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        padding: '24px', 
        border: '1px solid #e2e8f0', 
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.02)',
        overflowX: 'auto'
      }}>
        <div style={{ width: ledgerData.length > 5 ? calculatedWidth : '100%', height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={ledgerData}
              layout="horizontal"
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <defs>
                <linearGradient id="receiptsGrad" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#bbf7d0" /><stop offset="100%" stopColor="#16a34a" />
                </linearGradient>
                <linearGradient id="paymentsGrad" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#fca5a5" /><stop offset="100%" stopColor="#dc2626" />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              
              <XAxis 
                dataKey="Account_Name" 
                type="category"
                tick={{ fill: '#0f172a', fontSize: 11, fontWeight: 500 }} 
                axisLine={{ stroke: '#cbd5e1' }}
                tickLine={false}
                interval={0}
                angle={-20}
                textAnchor="end"
              />

              <YAxis 
                type="number"
                tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} 
                tick={{ fill: '#64748b', fontSize: 12 }} 
                axisLine={false}
                tickLine={false}
                width={55}
              />
              
              <Tooltip 
                formatter={(value) => value}
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: '8px', 
                  border: '1px solid #e2e8f0',
                  color: '#334155',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                }}
                cursor={{ fill: '#f8fafc' }}
              />
              
              <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '13px', fontWeight: 500 }} iconType="circle" iconSize={10} />
              
              <Bar dataKey="Receipts" fill="url(#receiptsGrad)" name="Receipts (+)" radius={[4, 4, 0, 0]} maxBarSize={30} />
              <Bar dataKey="Payments" fill="url(#paymentsGrad)" name="Payments (-)" radius={[4, 4, 0, 0]} maxBarSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}